import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {usePermissions} from '@/features/auth/store/store'
import {PatientDetailsEditButtons} from '@/features/patients/components/patient-details-edit-buttons/PatientDetailsEditButtons'
import {PatientVademecumDetails} from '@/features/patients/components/patient-vademecum-details/PatientVademecumDetails'
import {PatientVademecumFields} from '@/features/patients/components/patient-vademecum-fields/PatientVademecumFields'
import {usePatientEditModeView} from '@/features/patients/hooks/usePatientEditModeView'
import {usePatientFormSubmission} from '@/features/patients/hooks/usePatientFormSubmission'
import {useUpdatePatient} from '@/features/patients/services/useUpdatePatient'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {PatientExtended, PatientVademecumFormValidationSchema} from '@/features/patients/types'
import {
    getPatientDirtyFieldsFromFormValues,
    remapPatientToDefaultValues,
    remapPatientToPayload
} from '@/features/patients/utils'
import {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {useOutletContext} from 'react-router-dom'

export const PatientDetailsVademecum = () => {
    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const form = useForm<PatientVademecumFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientVademecumFormValidationSchema),
        defaultValues: remapPatientToDefaultValues(patient)
    })

    return (
        <FormProvider {...form}>
            <PatientDetailsVademecumContent patient={patient} />
        </FormProvider>
    )
}

const PatientDetailsVademecumContent = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = usePatientEditModeView('vademecum')
    const permissions = usePermissions()
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const form = useFormContext<PatientVademecumFormValidationSchema>()

    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = usePatientFormSubmission({
        formSchema: PatientVademecumFormValidationSchema
    })

    // mutation
    const updatePatient = useUpdatePatient({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getPatientDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapPatientToPayload(dirtyFormValues)
            return updatePatient.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('patients:vademecum')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.patients?.write && (
                    <PatientDetailsEditButtons
                        groupName="vademecum"
                        isConfirmDisabled={!form.formState.isDirty}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                    />
                )
            }
        >
            {isEditModeEnabled ? <PatientVademecumFields /> : <PatientVademecumDetails patient={patient} />}
        </DetailsFieldsSection>
    )
}
